import React, { useEffect, useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem("adminToken");
      setIsLoggedIn(!!token);
    };

    checkAuthStatus();
    
    // Listen for storage changes (in case user logs in/out in another tab)
    window.addEventListener('storage', checkAuthStatus);
    
    // Custom event listener for same-tab auth changes
    window.addEventListener('authStatusChanged', checkAuthStatus);
    
    return () => {
      window.removeEventListener('storage', checkAuthStatus);
      window.removeEventListener('authStatusChanged', checkAuthStatus);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setIsLoggedIn(false);
    navigate('/');
    window.dispatchEvent(new Event('authStatusChanged'));
  };
  

  const navLinks = [
    { label: 'About', href: '/about' },
    {
      label: 'Programs',
      submenu: [
        { label: 'Professionals', href: '/programs' },
        { label: 'Enterprise', href: '/enterprise' },
      ]
    },
    { label: 'Blog', href: '/blog' },
    ...(isLoggedIn ? [{ label: 'Dashboard', href: '/choosenone', isAdmin: true }] : []),
    isLoggedIn 
      ? { label: 'Logout', onClick: handleLogout, isButton: false }
      : { label: 'Login', href: '/login' },
    { label: 'Get In Touch', href: '/contact-us', isButton: true },
  ];

  return (
    <div className="bg-transparent">
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ease-out
          ${isSticky 
            ? 'bg-white/95 backdrop-blur-lg shadow-[0_8px_32px_rgba(0,0,0,0.1)] py-3 sm:py-4' 
            : 'bg-transparent py-4 sm:py-6 md:py-8'
          } px-4 sm:px-6`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center justify-center">
              <Link to="/" className="group">
                <div
                  className={`transition-all duration-300 ${
                    isSticky
                      ? 'text-black text-xl sm:text-2xl'
                      : 'text-white text-xl sm:text-2xl'
                  }`}
                >
                  <span className="font-thin tracking-[0.2em] uppercase">
                    Frost & Grey
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-10 xl:space-x-12">
              {navLinks.map((link, idx) => (
                <div key={idx} className="relative group">
                  {link.submenu ? (
                    <>
                      <button
                        className={`flex items-center space-x-2 transition-all duration-300 group-hover:tracking-[0.15em] ${
                          isSticky
                            ? 'text-black/70 hover:text-black text-[10px] md:text-xs tracking-[0.1em] uppercase font-light'
                            : 'text-white/80 hover:text-white text-[10px] md:text-xs tracking-[0.1em] uppercase font-light'
                        }`}
                      >
                        <span>{link.label}</span>
                        <ChevronDown className="w-3 h-3 transition-transform duration-300 group-hover:rotate-180" />
                      </button>
                      
                      {/* Dropdown */}
                      <div className="absolute left-0 mt-4 w-56 bg-white/95 backdrop-blur-lg rounded-none shadow-[0_8px_32px_rgba(0,0,0,0.15)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                        <div className="py-3">
                          {link.submenu.map((item, i) => (
                            <Link 
                              key={i} 
                              to={item.href} 
                              className="block px-6 py-2.5 text-black/70 hover:text-black hover:bg-black/5 text-[10px] md:text-xs tracking-[0.1em] uppercase font-light transition-all duration-200 hover:tracking-[0.15em]"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : link.isButton ? (
                    <Link
                      to={link.href}
                      className={`px-6 xl:px-8 py-2.5 xl:py-3 transition-all duration-300 text-[10px] md:text-xs tracking-[0.1em] uppercase font-light hover:tracking-[0.15em] ${
                        isSticky 
                          ? 'text-black border border-black/30 hover:bg-black hover:text-white hover:border-black' 
                          : 'text-white border border-white/40 hover:bg-white hover:text-black hover:border-white'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ) : link.onClick ? (
                    <button
                      onClick={link.onClick}
                      className={`transition-all duration-300 hover:tracking-[0.15em] ${
                        isSticky 
                          ? 'text-black/70 hover:text-black text-[10px] md:text-xs tracking-[0.1em] uppercase font-light' 
                          : 'text-white/80 hover:text-white text-[10px] md:text-xs tracking-[0.1em] uppercase font-light'
                      }`}
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className={`transition-all duration-300 hover:tracking-[0.15em] ${
                        link.isAdmin 
                          ? (isSticky 
                              ? 'text-black font-light tracking-[0.1em] uppercase text-[10px] md:text-xs hover:text-black/70' 
                              : 'text-white font-light tracking-[0.1em] uppercase text-[10px] md:text-xs hover:text-white/70')
                          : (isSticky 
                              ? 'text-black/70 hover:text-black text-[10px] md:text-xs tracking-[0.1em] uppercase font-light' 
                              : 'text-white/80 hover:text-white text-[10px] md:text-xs tracking-[0.1em] uppercase font-light')
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile menu button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`lg:hidden p-2 transition-colors duration-300 ${
                isSticky ? 'text-black hover:text-black/70' : 'text-white hover:text-white/70'
              }`}
              aria-label="Toggle navigation menu"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div
              className={`lg:hidden mt-6 rounded-none transition-all duration-300 ${
                isSticky
                  ? 'bg-white/95 backdrop-blur-lg shadow-[0_8px_32px_rgba(0,0,0,0.1)]'
                  : 'bg-black/90 backdrop-blur-lg'
              }`}
            >
              {/* Top line */}
              <div
                className={`w-full h-px ${
                  isSticky
                    ? 'bg-gradient-to-r from-transparent via-black/20 to-transparent'
                    : 'bg-gradient-to-r from-transparent via-white/30 to-transparent'
                }`}
              />

              <div className="py-6 px-4 sm:px-6 max-h-[70vh] overflow-y-auto">
                {navLinks.map((link, idx) => (
                  <div key={idx} className="mb-5 last:mb-0">
                    {link.submenu ? (
                      <div>
                        <button
                          type="button"
                          onClick={() =>
                            setOpenMobileSubmenu(
                              openMobileSubmenu === link.label ? null : link.label
                            )
                          }
                          className={`flex items-center justify-between w-full mb-2 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-light ${
                            isSticky ? 'text-black/60' : 'text-white/60'
                          }`}
                        >
                          <span>{link.label}</span>
                          <ChevronDown
                            className={`w-4 h-4 ml-2 transition-transform ${
                              openMobileSubmenu === link.label ? 'rotate-180' : ''
                            }`}
                          />
                        </button>

                        {openMobileSubmenu === link.label && (
                          <div className="ml-2 sm:ml-4 space-y-2 mt-2">
                            {link.submenu.map((item, i) => (
                              <Link
                                key={i}
                                to={item.href}
                                onClick={() => setIsMenuOpen(false)}
                                className={`block text-base sm:text-lg font-light tracking-wide transition-all duration-200 hover:tracking-[0.05em] ${
                                  isSticky
                                    ? 'text-black/80 hover:text-black'
                                    : 'text-white/80 hover:text-white'
                                }`}
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : link.onClick ? (
                      <button
                        onClick={() => {
                          link.onClick();
                          setIsMenuOpen(false);
                        }}
                        className={`block w-full text-left text-base sm:text-lg font-light tracking-wide transition-all duration-200 hover:tracking-[0.05em] ${
                          isSticky
                            ? 'text-black/80 hover:text-black'
                            : 'text-white/80 hover:text-white'
                        }`}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link
                        to={link.href}
                        onClick={() => setIsMenuOpen(false)}
                        className={`block text-base sm:text-lg font-light tracking-wide transition-all duration-200 hover:tracking-[0.05em] ${
                          link.isButton || link.isAdmin
                            ? isSticky
                              ? 'text-black font-medium'
                              : 'text-white font-medium'
                            : isSticky
                              ? 'text-black/80 hover:text-black'
                              : 'text-white/80 hover:text-white'
                        }`}
                      >
                        {link.label}
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              {/* Bottom line */}
              <div
                className={`w-full h-px ${
                  isSticky
                    ? 'bg-gradient-to-r from-transparent via-black/20 to-transparent'
                    : 'bg-gradient-to-r from-transparent via-white/30 to-transparent'
                }`}
              />
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
